import * as Sentry from "@sentry/react-native";
import { useQuery } from "@tanstack/react-query";
import * as AuthSession from "expo-auth-session";
import { router } from "expo-router";
import { ParseError, parsePhoneNumberWithError } from "libphonenumber-js";
import { useField, useForm } from "mobx-easy-form";
import { observer, Observer } from "mobx-react";
import { useCallback } from "react";
import { FormattedMessage } from "react-intl";
import * as yup from "yup";
import { z } from "zod";
import { Icon } from "~/components/Icon";
import { Spacer } from "~/components/Spacer";
import { ButtonV2 } from "~/components/V2/Button";
import { ErrorViewV2 } from "~/components/V2/ErrorViewV2";
import { SpinnerV2 } from "~/components/V2/SpinnerV2";
import { BodyText } from "~/components/V2/Text/BodyText";
import { BrandText } from "~/components/V2/Text/BrandText";
import { TextInputV2 } from "~/components/V2/TextInputV2";
import { View } from "~/components/V2/View";
import { useErrorAlertV2 } from "~/components/V2/error-alert-v2";
import { environment } from "~/environment";
import { useStore } from "~/mobx/utils/useStore";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { assert } from "~/utils/assert";
import { useProgressBarStep } from "./useProgressBarStep";
import { useStandaloneBookingFlow } from "./useStandaloneBookingFlow";
import { withBookingData } from "./withBookingData";

// Schema for BankID
const bankIdSchema = z.object({
  name: z.string(),
  given_name: z.string(),
  family_name: z.string(),
  idp_issuer: z.string(),
});

// Schema for Email OTP
const emailOtpSchema = z.object({
  idp_id: z.string().optional(),
  sub: z.string().optional(),
  idp_issuer: z.string(),
});
interface AddClientDetailsScreenWithAuthDataProps {
  authName: string;
  authGivenName: string;
  authFamilyName: string;
}

export function AddClientDetailsScreenWithAuthData({
  authName,
  authGivenName,
  authFamilyName,
}: AddClientDetailsScreenWithAuthDataProps) {
  const store = useStore();
  const errorAlert = useErrorAlertV2();
  useProgressBarStep(7);

  const { clinicId, isNewUser, isStandAloneBookingFlow } =
    useStandaloneBookingFlow();

  const form = useForm({
    onSubmit: async ({ values }) => {
      const valuesSchema = z.object({
        name: z.string(),
        phoneNumber: z.string(),
        email: z.string(),
        address: z.string(),
        postalNumber: z.string(),
        city: z.string(),
      });

      const valuesParsed = valuesSchema.parse(values);

      store.bookingStore.setNewClientDetails({
        newClientFirstName: authGivenName,
        newClientLastName: authFamilyName,
        newClientStreetAddress: valuesParsed.address,
        newClientZipCode: valuesParsed.postalNumber,
        newClientEmail: valuesParsed.email,
        newClientCity: valuesParsed.city,
        newClientPhoneNumber: valuesParsed.phoneNumber,
      });

      // this screen is for now only used in standalone flow so this check doesnt make sense at the moment
      // but we can keep it just in case we decide to use this screen in other flows
      if (isStandAloneBookingFlow) {
        router.navigate({
          pathname: "/(standalone)/booking/overview",
          params: {
            clinicId,
            isNewUser: isNewUser ? "1" : "0",
          },
        });
      } else {
        router.navigate({
          pathname: "/(app)/book-appointment/overview",
        });
      }
    },
  });

  // Functions to get initial values for the form fields
  const getInitialFirstName = useCallback(() => {
    if (authName) {
      return authName;
    } else if (
      !!store.bookingStore.newClientFirstName &&
      !!store.bookingStore.newClientLastName
    ) {
      return `${store.bookingStore.newClientFirstName} ${store.bookingStore.newClientLastName}`;
    } else if (__DEV__) {
      return "John Doe";
    } else {
      return "";
    }
  }, [
    authName,
    store.bookingStore.newClientFirstName,
    store.bookingStore.newClientLastName,
  ]);
  const getInitialPhoneNumber = useCallback(() => {
    if (store.bookingStore.newClientPhoneNumber) {
      return store.bookingStore.newClientPhoneNumber;
    } else if (__DEV__) {
      return "+46726416273";
    } else {
      return environment.SUPPORT_PHONE_NUMBER_COUNTRY_CODE;
    }
  }, [store.bookingStore.newClientPhoneNumber]);
  const getInitialEmail = useCallback(() => {
    if (store.bookingStore.newClientEmail) {
      return store.bookingStore.newClientEmail;
    } else if (__DEV__) {
      return "example@ivcevidensia.com";
    } else {
      return "";
    }
  }, [store.bookingStore.newClientEmail]);
  const getInitialAddress = useCallback(() => {
    if (store.bookingStore.newClientStreetAddress) {
      return store.bookingStore.newClientStreetAddress;
    } else if (__DEV__) {
      return "Kungsgatan";
    } else {
      return "";
    }
  }, [store.bookingStore.newClientStreetAddress]);
  const getInitialZipCode = useCallback(() => {
    if (store.bookingStore.newClientZipCode) {
      return store.bookingStore.newClientZipCode;
    } else if (__DEV__) {
      return "753 21";
    } else {
      return "";
    }
  }, [store.bookingStore.newClientZipCode]);
  const getInitialCity = useCallback(() => {
    if (store.bookingStore.newClientCity) {
      return store.bookingStore.newClientCity;
    } else if (__DEV__) {
      return "Uppsala";
    } else {
      return "";
    }
  }, [store.bookingStore.newClientCity]);
  // End of functions to get initial values for the form fields

  const name = useField({
    id: "name",
    form,
    initialValue: getInitialFirstName(),
    validationSchema: yup.string().required(
      intl.formatMessage({
        id: "9gOiO+",
        defaultMessage: "Name is required",
        description: "Error message for empty name",
      }),
    ),
  });

  const phoneNumber = useField(
    {
      id: "phoneNumber",
      form,
      initialValue: getInitialPhoneNumber(),
      validate: (value) => {
        if (!!value && value.trim()) {
          try {
            const parsedValue = parsePhoneNumberWithError(value, {
              extract: false,
            });

            return {
              error: undefined,
              parsed: parsedValue.formatInternational(),
            };
          } catch (error) {
            if (error instanceof ParseError) {
              if (error.message === "TOO_LONG") {
                return {
                  error: intl.formatMessage({
                    id: "pB8SjL",
                    description: "Error message for too long phone number",
                    defaultMessage: "The phone number is too long",
                  }),
                  parsed: undefined,
                };
              } else if (error.message === "TOO_SHORT") {
                return {
                  error: intl.formatMessage({
                    id: "l8lO/0",
                    description: "Error message for too short phone number",
                    defaultMessage: "The phone number is too short",
                  }),
                  parsed: undefined,
                };
              } else {
                return {
                  error: intl.formatMessage({
                    id: "N/1f86",
                    description: "Error message for invalid phone number",
                    defaultMessage: "The phone number you entered is not valid",
                  }),
                  parsed: undefined,
                };
              }
            } else {
              errorAlert(error);
              Sentry.captureException(error);
              return {
                error: intl.formatMessage({
                  id: "N/1f86",
                  description: "Error message for invalid phone number",
                  defaultMessage: "The phone number you entered is not valid",
                }),
                parsed: undefined,
              };
            }
          }
        }
        return {
          error: intl.formatMessage({
            id: "KBojtB",
            description: "Error message for empty phone number",
            defaultMessage: "Phone number is required",
          }),
          parsed: undefined,
        };
      },
    },
    [],
  );
  const email = useField({
    id: "email",
    form,
    initialValue: getInitialEmail(),
    validationSchema: yup
      .string()
      .email(
        intl.formatMessage({
          id: "295JUa",
          defaultMessage: "The email you entered is invalid",
          description: "Error message for invalid email",
        }),
      )
      .required(
        intl.formatMessage({
          id: "GoWwI6",
          defaultMessage: "Email is required",
          description: "Error message for empty email",
        }),
      ),
  });
  const address = useField({
    id: "address",
    form,
    initialValue: getInitialAddress(),
    validationSchema: yup.string().required(
      intl.formatMessage({
        id: "L6ID4a",
        defaultMessage: "Address is required",
        description: "Error message for empty address",
      }),
    ),
  });
  const postalNumber = useField({
    id: "postalNumber",
    form,
    initialValue: getInitialZipCode(),
    validationSchema: yup.string().required(
      intl.formatMessage({
        id: "gFpun4",
        defaultMessage: "Postal number is required",
        description: "Error message for empty postal number",
      }),
    ),
  });
  const city = useField({
    id: "city",
    form,
    initialValue: getInitialCity(),
    validationSchema: yup.string().required(
      intl.formatMessage({
        id: "a8h+Yh",
        defaultMessage: "City is required",
        description: "Error message for empty city",
      }),
    ),
  });

  return (
    <View flex colorWhite>
      <View paddingVertical32 paddingHorizontal24>
        <BrandText sizeH1 weightExtraBold alignCenter colorBlack>
          <FormattedMessage
            defaultMessage="Your information"
            id="M/DGFF"
            description="Title for client details screen"
          />
        </BrandText>
        <Spacer extraExtraLarge />

        <Observer>
          {() => {
            return (
              <TextInputV2
                label={intl.formatMessage({
                  id: "5VPo0E",
                  defaultMessage: "Name",
                  description: "First name label",
                })}
                value={name.state.value}
                disabled={!!authName}
                onFocus={name.actions.onFocus}
                onBlur={name.actions.onBlur}
                onChangeText={name.actions.onChange}
                rightComponent={
                  !!authName && (
                    <Icon
                      name="lock-outline"
                      size={24}
                      color={styleConstantsV2.colorBlack}
                    />
                  )
                }
                isError={!!name.computed.ifWasEverBlurredThenError}
              />
            );
          }}
        </Observer>

        <Spacer large />
        <Observer>
          {() => {
            return (
              <TextInputV2
                label={intl.formatMessage({
                  id: "DWtSWO",
                  defaultMessage: "Phone number",
                  description: "Phone number label",
                })}
                value={phoneNumber.state.value}
                onBlur={phoneNumber.actions.onBlur}
                onFocus={phoneNumber.actions.onFocus}
                onChangeText={phoneNumber.actions.onChange}
                isError={!!phoneNumber.computed.ifWasEverBlurredThenError}
                textBelowComponent={
                  phoneNumber.computed.ifWasEverBlurredThenError ? (
                    <BodyText sizeSmall colorError>
                      {phoneNumber.computed.error}
                    </BodyText>
                  ) : undefined
                }
              />
            );
          }}
        </Observer>
        <Spacer large />
        <Observer>
          {() => {
            return (
              <TextInputV2
                label={intl.formatMessage({
                  id: "dRMELz",
                  defaultMessage: "E-mail",
                  description: "Email label",
                })}
                value={email.state.value}
                onBlur={email.actions.onBlur}
                onFocus={email.actions.onFocus}
                onChangeText={email.actions.onChange}
                isError={!!email.computed.ifWasEverBlurredThenError}
                textBelowComponent={
                  email.computed.ifWasEverBlurredThenError ? (
                    <BodyText sizeSmall colorError>
                      {email.computed.error}
                    </BodyText>
                  ) : undefined
                }
              />
            );
          }}
        </Observer>
        <Spacer large />
        <Observer>
          {() => {
            return (
              <TextInputV2
                label={intl.formatMessage({
                  id: "Oj0iQH",
                  defaultMessage: "Address",
                  description: "Address label",
                })}
                value={address.state.value}
                onBlur={address.actions.onBlur}
                onFocus={address.actions.onFocus}
                onChangeText={address.actions.onChange}
                isError={!!address.computed.ifWasEverBlurredThenError}
                textBelowComponent={
                  address.computed.ifWasEverBlurredThenError ? (
                    <BodyText sizeSmall colorError>
                      {address.computed.error}
                    </BodyText>
                  ) : undefined
                }
              />
            );
          }}
        </Observer>
        <Spacer large />
        <Observer>
          {() => {
            return (
              <TextInputV2
                label={intl.formatMessage({
                  id: "0nJ1MV",
                  defaultMessage: "Postal number",
                  description: "Postal number input label",
                })}
                value={postalNumber.state.value}
                onBlur={postalNumber.actions.onBlur}
                onFocus={postalNumber.actions.onFocus}
                onChangeText={postalNumber.actions.onChange}
                isError={!!postalNumber.computed.ifWasEverBlurredThenError}
                textBelowComponent={
                  postalNumber.computed.ifWasEverBlurredThenError ? (
                    <BodyText sizeSmall colorError>
                      {postalNumber.computed.error}
                    </BodyText>
                  ) : undefined
                }
              />
            );
          }}
        </Observer>
        <Spacer large />
        <Observer>
          {() => {
            return (
              <TextInputV2
                label={intl.formatMessage({
                  id: "mMWP1r",
                  defaultMessage: "City",
                  description: "City label",
                })}
                value={city.state.value}
                onBlur={city.actions.onBlur}
                onFocus={city.actions.onFocus}
                onChangeText={city.actions.onChange}
                isError={!!city.computed.ifWasEverBlurredThenError}
                textBelowComponent={
                  city.computed.ifWasEverBlurredThenError ? (
                    <BodyText sizeSmall colorError>
                      {city.computed.error}
                    </BodyText>
                  ) : undefined
                }
              />
            );
          }}
        </Observer>
      </View>
      <View
        flex
        flexDirectionColumnReverse
        paddingHorizontal24
        paddingVertical32
        colorWhite
      >
        <Observer>
          {() => {
            return (
              <ButtonV2
                title={intl.formatMessage({
                  id: "3Wcqsy",
                  defaultMessage: "Next",
                  description: "Next button",
                })}
                onPress={form.actions.submit}
                disabled={form.computed.isError && form.state.submitCount > 0}
              />
            );
          }}
        </Observer>
      </View>
    </View>
  );
}

export const AddClientDetailsScreen = observer(
  function AddClientDetailsScreen() {
    const store = useStore();

    const query = useQuery({
      queryKey: ["newUserDetails"],
      queryFn: async () => {
        const accessToken = store.authStore.accessToken;

        assert(accessToken, "Access token should exist");

        const discovery = await AuthSession.resolveDiscoveryAsync(
          environment.OIDC_DISCOVERY_URL,
        );

        const userDetails = await AuthSession.fetchUserInfoAsync(
          {
            accessToken,
          },
          discovery,
        );

        const providerSchema = z.object({
          idp_issuer: z.string(),
        });
        const { idp_issuer } = providerSchema.parse(userDetails);

        if (
          idp_issuer === "otp-email" &&
          environment.ENVIRONMENT_NAME === "prod"
        ) {
          throw new Error("OTP email is not supported in production");
        }

        let name: string, given_name: string, family_name: string;

        // This is an ugly fix to get the name to show in the form
        // If we ever need to use emiil OTP in production, we should fix this
        if (idp_issuer === "otp-email") {
          emailOtpSchema.parse(userDetails);

          name = "Test User";
          given_name = "Test";
          family_name = "User";
        } else {
          // Validate with BankID schema (assumes non-OTP is BankID for now)
          const parsedDetails = bankIdSchema.parse(userDetails);
          name = parsedDetails.name;
          given_name = parsedDetails.given_name;
          family_name = parsedDetails.family_name;
        }

        return {
          name,
          given_name,
          family_name,
          idp_issuer,
        };
      },
    });

    if (query.isLoading) {
      return (
        <View colorWhite flex>
          <View padding32 centerContent>
            <SpinnerV2 />
          </View>
        </View>
      );
    }

    if (query.isError) {
      return (
        <View colorWhite flex>
          <View centerContent paddingHorizontal24>
            <ErrorViewV2
              error={query.error}
              title={intl.formatMessage({
                id: "OTjx7h",
                defaultMessage: "Something went wrong",
                description: "Error message title when fetching pet list fails",
              })}
            />
            <ButtonV2
              onPress={query.refetch}
              title={intl.formatMessage({
                id: "lTcTd0",
                defaultMessage: "Try Again!",
                description: "Button to retry fetching pet list",
              })}
            />
          </View>
        </View>
      );
    }

    if (!query.data) {
      throw new Error("User details should exist");
    }

    const authName = query.data.name;
    const authFamilyName = query.data.family_name;
    const authGivenName = query.data.given_name;

    return (
      <AddClientDetailsScreenWithAuthData
        authName={authName}
        authFamilyName={authFamilyName}
        authGivenName={authGivenName}
      />
    );
  },
);

// If any required data from earlier in the booking process is missing, redirect to the first step
export const AddClientDetailsScreenWithBookingData = withBookingData(
  AddClientDetailsScreen,
  [
    "clinicId",
    "patientId",
    "reasonId",
    "practitionerId",
    "start",
    "end",
    "clientNotes",
  ],
);
